import type {
  ReplyEmailItem,
  ReplyResponseStatus,
} from "hooks/use-email-replies-api";
import { useEffect } from "react";

import type { ReplyLabel } from "routes/emails/EmailRepliesPage/components/ReplyFilterBar";

export type UseReplyKeyboardShortcutsOptions = {
  items: ReplyEmailItem[];
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  toggleChecked: (id: string) => void;
  onSetStatusCurrent: (status: ReplyResponseStatus) => void;
  onStartReply: () => void;
  /** ラベルセグメント前後移動 (`[` `]`) */
  onMoveLabel: (direction: -1 | 1) => void;
  /** 検索ボックスへフォーカス (`/`) */
  onFocusSearch: () => void;
};

const LABEL_ORDER: ReadonlyArray<ReplyLabel> = [
  "all",
  "delivery",
  "sent_email",
];

/**
 * キーボードショートカット.
 *  j / k = 前後移動 / x = チェック / e = 完了 / i = 対応中 / 1〜3 = ステータス
 *  r = 返信フォームを開く / [ ] = ラベル前後 / / = 検索フォーカス
 * 旧 t (スレッドモーダル) と o (詳細表示) はインライン化により廃止.
 * @param {UseReplyKeyboardShortcutsOptions} options options
 * @return {void}
 */
export function useReplyKeyboardShortcuts(
  options: UseReplyKeyboardShortcutsOptions,
): void {
  const {
    items,
    selectedId,
    setSelectedId,
    toggleChecked,
    onSetStatusCurrent,
    onStartReply,
    onMoveLabel,
    onFocusSearch,
  } = options;

  useEffect(() => {
    /**
     * キーハンドラ
     * @param {KeyboardEvent} e キーイベント
     * @return {void}
     */
    const handler = (e: KeyboardEvent): void => {
      // input / textarea にフォーカス中はスルー (検索フォーカス `/` を除く)
      const target = e.target as HTMLElement | null;
      const inEditable =
        target !== null &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (inEditable && e.key !== "Escape") return;

      const idx = items.findIndex(r => r.id === selectedId);
      switch (e.key) {
        case "j": {
          if (items.length === 0) return;
          const next = idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
          setSelectedId(items[next].id);
          e.preventDefault();
          break;
        }
        case "k": {
          if (items.length === 0) return;
          const next = idx < 0 ? 0 : Math.max(idx - 1, 0);
          setSelectedId(items[next].id);
          e.preventDefault();
          break;
        }
        case "x": {
          if (selectedId) {
            toggleChecked(selectedId);
            e.preventDefault();
          }
          break;
        }
        case "e": {
          if (selectedId) {
            onSetStatusCurrent("completed");
            e.preventDefault();
          }
          break;
        }
        case "i":
        case "2": {
          if (selectedId) {
            onSetStatusCurrent("in_progress");
            e.preventDefault();
          }
          break;
        }
        case "1": {
          if (selectedId) {
            onSetStatusCurrent("unresponded");
            e.preventDefault();
          }
          break;
        }
        case "3": {
          if (selectedId) {
            onSetStatusCurrent("completed");
            e.preventDefault();
          }
          break;
        }
        case "r": {
          onStartReply();
          e.preventDefault();
          break;
        }
        case "[": {
          onMoveLabel(-1);
          e.preventDefault();
          break;
        }
        case "]": {
          onMoveLabel(1);
          e.preventDefault();
          break;
        }
        case "/": {
          onFocusSearch();
          e.preventDefault();
          break;
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    items,
    selectedId,
    setSelectedId,
    toggleChecked,
    onSetStatusCurrent,
    onStartReply,
    onMoveLabel,
    onFocusSearch,
  ]);
}

/**
 * ラベルを順番に進める / 戻すヘルパ.
 * @param {ReplyLabel} current 現在のラベル
 * @param {-1 | 1} direction 方向
 * @return {ReplyLabel} 次のラベル
 */
export function nextLabel(current: ReplyLabel, direction: -1 | 1): ReplyLabel {
  const idx = LABEL_ORDER.indexOf(current);
  const next = (idx + direction + LABEL_ORDER.length) % LABEL_ORDER.length;
  return LABEL_ORDER[next];
}
